/* eslint-disable comma-dangle */
/* eslint-disable max-len */
import * as Sentry from '@sentry/browser';
import { useEffect, useMemo, useState } from 'react';
import isNil from 'lodash/isNil';
import { GqlClient } from '@search/gql-client/src';
import { BuyableVasGqlEnum, PlacementDaysPrice, OrderOfferVasOfferParam } from '@search/graphql-typings';
import { CurrencyEnum } from '@search/graphql-typings';
import { VasPeriod } from '../enums';
import { VasData, VasParams } from '../types/vas';

import { GetPricesAllQuery } from '../queries/GetPricesAllQuery';
import { GetPricesAllQuery$data as GetPricesAllQueryResponse, GetPricesAllQuery$variables as GetPricesAllQueryVariables } from '../queries/__generated__/GetPricesAllQuery.graphql';

export type PriceType = PlacementDaysPrice;

type IGetPricesService = {
    gql: GqlClient;
    offersParams: OrderOfferVasOfferParam[];
    promocode?: string;
}

type IGetVasPricesAllService = {
    top: PriceType[];
    highlight: PriceType[];
    topHighlight: PriceType[];
}

export const getVasPricesAllService = ({ gql, offersParams, promocode }: IGetPricesService): Promise<IGetVasPricesAllService> => {
    return new Promise((resolve, reject) => {
        gql.execute<GetPricesAllQueryVariables, GetPricesAllQueryResponse>(
            GetPricesAllQuery, { offersParams, promocode }
        )
            .then(res => {
                if (! isNil(res.data) && ! isNil(res.data.getPricesAll)) {
                    const { top, highlight, topHighlight } = res.data.getPricesAll;

                    return resolve({
                        top: top as PriceType[],
                        highlight: highlight as PriceType[],
                        topHighlight: topHighlight as PriceType[],
                    });
                }

                if (! isNil(res.errors)) {
                    throw (res.errors);
                }

                throw new Error('Empty getPricesAll');
            })
            .catch(e => {
                if (typeof Sentry !== 'undefined') {
                    Sentry.withScope(() => {
                        Sentry.setExtra('error', e);
                        Sentry.setExtra('params', { offersParams });

                        Sentry.captureException(e, {
                            tags: {
                                section: 'GQL',
                                method: 'getVasPricesAllService'
                            }
                        });
                    });
                }
                reject(e);
            });
    });
};

const RegionIdMsk = 3;

export const OfferIdMocked = '00000000-0000-0000-0000-000000000000';

const periodMap: Record<number, VasPeriod> = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    7: VasPeriod.WEEK,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    14: VasPeriod.TWO_WEEKS,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    30: VasPeriod.MONTH
};

const convertDate = (localDate: {
    day: number;
    month: number;
    year: number;
}): Date => {
    const date = new Date();

    date.setFullYear(localDate.year);
    date.setMonth(localDate.month - 1);
    date.setDate(localDate.day);

    return date;
};

export const convertToVasData = (items: PriceType[]): VasData[] => {
    return items.map((item: PriceType) => {
        const {
            daysAmount,
            dateTill,
            daysQuantityDiscountPercent,
            offersDaysDiscountPrice,
            offersAmount,
            offersQuantityDiscountPercent,
            offersQuantityDiscount,
            totalBasePrice,
            totalPrice,
            offersPacketDiscount,
            offersPacketDiscountPercent,
            promocodeInfo,
        } = item;

        return {
            count: offersAmount,
            period: periodMap[daysAmount],
            priceBeforeDiscount: offersDaysDiscountPrice !== totalBasePrice ?
                totalBasePrice * 100 : undefined,
            price: offersDaysDiscountPrice * 100,
            totalPrice: totalPrice * 100,
            totalPriceBeforeDiscount: totalBasePrice * 100,
            currency: CurrencyEnum.Rub,
            days: daysAmount,

            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            untilDate: dateTill ? convertDate(dateTill) : new Date(),
            discount: daysQuantityDiscountPercent > 0 ? daysQuantityDiscountPercent : undefined,
            discountOnTop: offersPacketDiscount > 0,

            quantityDiscount: offersQuantityDiscount > 0 ? offersQuantityDiscount * 100 : undefined,
            quantityDiscountPercent: offersQuantityDiscountPercent > 0 ? offersQuantityDiscountPercent : undefined,

            packetDiscount: offersPacketDiscount > 0 ? offersPacketDiscount * 100 : undefined,
            packetDiscountPercent: offersPacketDiscountPercent > 0 ? offersPacketDiscountPercent : undefined,

            promocodeInfo: ! isNil(promocodeInfo) ? {
                success: promocodeInfo.success,
                status: promocodeInfo.status,
                discountPercent: promocodeInfo.discountPercent ?? undefined,
                productPrefix: promocodeInfo.productPrefix ?? undefined,
            } : undefined
        };
    });
};

type UsePriceAllDataType = {
    vasDataMap: VasData[][];
    isVasDataLoading: boolean;
}

interface UseGetPricesDataProps {
    gql: GqlClient;
    offersParams: OrderOfferVasOfferParam[];
    promocode?: string;
}

export const useVasPricesData = ({
    gql,
    offersParams,
    promocode
}: UseGetPricesDataProps): UsePriceAllDataType => {
    const [ isLoading, setLoading ] = useState(true);
    const [ vasDataMap, setVasDataMap ] = useState<VasData[][]>([]);

    const params = offersParams.length ? offersParams : [ {
        offerId: OfferIdMocked,
        regionId: RegionIdMsk
    } ] as OrderOfferVasOfferParam[];

    useEffect(() => {
        getVasPricesAllService({ gql, offersParams: params, promocode })
            .then(({ top, highlight, topHighlight }) => {
                const p1 = convertToVasData(top);
                const p2 = convertToVasData(highlight);
                const p3 = convertToVasData(topHighlight);

                setVasDataMap([ p1, p2, p3 ]);
            })
            .finally(() => setLoading(false))
            .catch(() => {
                setVasDataMap([]);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ params, promocode ]);

    return {
        vasDataMap,
        isVasDataLoading: isLoading
    };
};

type UseGetPriceDataTye = {
    vasData: VasData[];
    selectedVasData?: VasData;
}

interface UseSelectedPricesDataProps {
    vasDataMap: VasData[][];
    vas: VasParams;
}

export const useSelectedVasPricesData = ({
    vasDataMap,
    vas
}: UseSelectedPricesDataProps): UseGetPriceDataTye => {
    const [ vasData, setVasData ] = useState<VasData[]>([]);

    const selectedVasData = useMemo(() => {
        return vas.types.length > 0 ? vasData.find(vasItem => vasItem.period === vas.period) : undefined;
    }, [ vas.period, vas.types, vasData ]);

    useEffect(() => {
        if (vas.types.length > 0 && vasDataMap.length > 0) {
            if (vas.types.length === 1) {
                if (vas.types[0] === BuyableVasGqlEnum.Top) {
                    setVasData(vasDataMap[0]);
                }
                if (vas.types[0] === BuyableVasGqlEnum.Highlight) {
                    setVasData(vasDataMap[1]);
                }
            } else {
                setVasData(vasDataMap[2]);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ vas.types, vasDataMap ]);

    return {
        vasData,
        selectedVasData
    };
};
